import React, { useRef, useState, useEffect } from 'react'
import { useReactToPrint } from 'react-to-print';
import { useParams } from 'react-router-dom';
import { useAlert } from '../../../../../../context/AlertContext';
import voucherServices from '../voucherServices';
import companyServices from '../../company/companyServices';
import { useAppData } from '../../../../../../context/AppDataContext';
import { CancelOutlined, CheckCircle, Email, Phone, Print, Person, LocationOn } from '@mui/icons-material';
import ExpenseVoucherForm from '../component/ExpenseVoucherForm';
import { NumberToWords } from '../section/NumberToWords';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import moment from 'moment/moment';

function ExpenseVoucherDetailsPage() {
    const { id } = useParams();
    const printComponentRef = useRef();
    const { AppAlert } = useAlert();
    const [dialogStatus, setDialogStatus] = useState(false);
    const { getMasterLabel, ledgerDD, getDDDataById, companyLocationDD } = useAppData();
    
    const [voucherDetails, setVoucherDetails] = useState(null);
    const [companyDetails, setCompanyDetails] = useState(null);

    const getVoucherDetails = () => {
        voucherServices.getById(id).then(res => {
            setVoucherDetails(res.data);
        }).catch(error => {
            console.error("Error fetching voucher:", error);
        })
    }

    const getCompanyDetails = (companyId) => {
        companyServices.getById(companyId).then(res => {
            setCompanyDetails(res.data);
        }).catch(error => {
            console.error("Error fetching company:", error);
        })
    }

    useEffect(() => {
        getVoucherDetails();
    }, [id])

    useEffect(() => {
        if (voucherDetails && voucherDetails.companyId) {
            getCompanyDetails(voucherDetails.companyId);
        }
    }, [voucherDetails])

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current,
        pageStyle: `
            @page { size: auto; margin: 10mm; }
            @media print { body { -webkit-print-color-adjust: exact; } }
        `
    });

    const finalize = () => {
        setDialogStatus(true);
        // Using a generic finalize or specific expense one if you created it
        voucherServices.expenseFinalize(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => {
            AppAlert(error.msg, "error")
        }).finally(() => {
            setDialogStatus(false);
        })
    }

    const discard = () => {
        if (!window.confirm("Are you sure you want to discard this expense?")) return;
        setDialogStatus(true);
        voucherServices.expenseDiscard(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => {
            AppAlert(error.msg, "error")
        }).finally(() => {
            setDialogStatus(false);
        })
    }

    return (
        <FinanceLayout>
            {voucherDetails && companyDetails &&
                <div className='container'>
                    <Dialog open={dialogStatus} keepMounted fullWidth maxWidth="sm">
                        <DialogContent className='text-center'>
                            <CircularProgress />
                            <div className='mt-2'>Processing Voucher...</div>
                        </DialogContent>
                    </Dialog>

                    <div className='row mb-3 no-print'>
                        <div className='col-md-12 d-flex align-items-center gap-2'>
                            {voucherDetails.status === 1 &&
                                <ExpenseVoucherForm data={voucherDetails} callBack={getVoucherDetails} />
                            }
                            
                            {voucherDetails.status === 2 ? (
                                <button className='btn btn-success'><CheckCircle /> Finalized</button>
                            ) : voucherDetails.status === 1 ? (
                                <button className='btn btn-primary' onClick={finalize}><CheckCircle /> Finalize</button>
                            ) : (
                                <button className='btn btn-danger' disabled>Discarded</button>
                            )}

                            <div className='ms-auto d-flex gap-2'>
                                {voucherDetails.status === 2 && 
                                    <button className='btn btn-outline-danger' onClick={discard}><CancelOutlined /> Discard</button>
                                }
                                <button className='btn btn-outline-primary' onClick={handlePrint}><Print /> Print</button>
                            </div>
                        </div>
                    </div>

                    <div ref={printComponentRef} className='p-4 bg-white border'>
                        {/* Company Header */}
                        <div className='row border-bottom pb-3'>
                            <div className='col-12 text-center'>
                                <h4 className='fw-bold mb-1'>{companyDetails.companyName}</h4>
                                <p className='mb-0 small'>{companyDetails.address}, {companyDetails.city}, {companyDetails.zip}</p>
                                <p className='mb-0 small'>GSTIN: <span className='fw-bold'>{companyDetails.gst.toUpperCase()}</span> | Email: {companyDetails.email}</p>
                            </div>
                        </div>

                        {/* Voucher Info */}
                        <div className='row my-3'>
                            <div className='col-12 text-center'>
                                <h5 className='fw-bold text-uppercase' style={{ letterSpacing: '1px' }}>Expense Voucher</h5>
                            </div>
                            <div className='col-6'>
                                <table className='small'>
                                    <tbody>
                                        <tr><td>Voucher No</td><td>: <span className='fw-bold'>{voucherDetails.voucherNo}</span></td></tr>
                                        <tr><td>Ref/Bill No</td><td>: {voucherDetails.refNo || 'N/A'}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className='col-6 text-end'>
                                <table className='small ms-auto'>
                                    <tbody>
                                        <tr><td>Date</td><td>: <span className='fw-bold'>{moment(voucherDetails.voucherDate).format('DD-MMM-YYYY')}</span></td></tr>
                                        <tr><td>Project</td><td>: {getDDDataById(companyLocationDD, voucherDetails.locationId)?.label || 'General'}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Main Table */}
                        <table className='table table-bordered'>
                            <thead className='bg-light'>
                                <tr>
                                    <th>Description / Account Head</th>
                                    <th className='text-end' width="25%">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ minHeight: '150px' }}>
                                    <td>
                                        <div className='fw-bold' style={{ fontSize: '1.1rem' }}>
                                            {getDDDataById(ledgerDD, voucherDetails.ledgerId)?.label}
                                        </div>
                                        <div className='text-muted mt-2 small'>
                                            <Person style={{ fontSize: 14 }} /> Spent By: {voucherDetails.employeeName}
                                        </div>
                                        <div className='mt-3'>
                                            <strong>Narration:</strong> {voucherDetails.narration}
                                        </div>
                                    </td>
                                    <td className='text-end align-middle'>
                                        <div className='fw-bold' style={{ fontSize: '1.1rem' }}>
                                            {Number(voucherDetails.taxableTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className='text-end small'>Taxable Total</td>
                                    <td className='text-end small'>{Number(voucherDetails.taxableTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                                {Number(voucherDetails.gstTotal) > 0 && (
                                    <tr>
                                        <td className='text-end small'>GST Total</td>
                                        <td className='text-end small'>{Number(voucherDetails.gstTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                )}
                                {Number(voucherDetails.roundOff) !== 0 && (
                                    <tr>
                                        <td className='text-end small'>Round Off</td>
                                        <td className='text-end small'>{voucherDetails.roundOff}</td>
                                    </tr>
                                )}
                                <tr className='bg-light'>
                                    <td className='text-end fw-bold'>Grand Total</td>
                                    <td className='text-end fw-bold' style={{ fontSize: '1.1rem' }}>
                                        ₹ {Number(voucherDetails.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        {/* Payment Context & Signatures */}
                        <div className='row mt-2'>
                            <div className='col-12 small'>
                                <strong>Amount in Words: </strong> 
                                <span className='text-capitalize'><NumberToWords amount={voucherDetails.grandTotal} /> Only</span>
                            </div>
                            <div className='col-12 mt-2 small'>
                                <strong>Payment Method: </strong> 
                                {getDDDataById(ledgerDD, voucherDetails.paymentLedgerId)?.label}
                            </div>
                        </div>

                        <div className='row mt-5 pt-4'>
                            <div className='col-4 text-center'>
                                <div style={{ borderTop: '1px solid #000' }} className='pt-2 small'>Employee/Receiver</div>
                            </div>
                            <div className='col-4'></div>
                            <div className='col-4 text-center'>
                                <div style={{ borderTop: '1px solid #000' }} className='pt-2 small'>Authorised Signatory</div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </FinanceLayout>
    )
}

export default ExpenseVoucherDetailsPage;